/* Circular SVG progress ring with goal name and progress text laid out horizontally. */

import { formatMinutes } from "@/components/goals/goalTimeOptions";

interface GoalProgressRingProps {
  name: string;
  icon?: string | null;
  completedMinutes: number;
  goalMinutes: number;
  streak?: number;
  onClick?: () => void;
}

const RING_SIZE = 28;
const STROKE_WIDTH = 2.5;

export function GoalProgressRing({
  name,
  icon,
  completedMinutes,
  goalMinutes,
  streak = 0,
  onClick,
}: GoalProgressRingProps) {
  const radius = (RING_SIZE - STROKE_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(completedMinutes / goalMinutes, 1);
  const dashOffset = circumference * (1 - progress);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 shrink-0 rounded-md px-1.5 py-1 hover:bg-accent transition-colors cursor-pointer"
    >
      <div className="relative shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
        <svg
          width={RING_SIZE}
          height={RING_SIZE}
          className="rotate-[-90deg]"
        >
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={STROKE_WIDTH}
            className="text-muted-foreground/25"
          />
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={STROKE_WIDTH}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            className="text-primary transition-[stroke-dashoffset] duration-300"
          />
        </svg>
        {icon ? (
          <span className="absolute inset-0 flex items-center justify-center text-xs leading-none">
            {icon}
          </span>
        ) : streak > 0 ? (
          <span className={`absolute inset-0 flex items-center justify-center text-[9px] font-bold ${progress >= 1 ? "text-primary" : "text-muted-foreground"}`}>
            {streak}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col leading-tight text-left">
        <span className="text-[11px] font-medium text-foreground truncate max-w-16">
          {name}
        </span>
        <span className="text-[10px] text-muted-foreground">
          {formatMinutes(completedMinutes)}/{formatMinutes(goalMinutes)}
        </span>
      </div>
    </button>
  );
}
